import React, { useState, useRef, useEffect, useCallback } from 'react';
import api from '../utils/api';

// Adelantador de citas ITV (Andalucía) — funcionalidad autenticada y de pago por créditos.
// Solo accesible desde el panel (/app/adelantador-itv). El backend descuenta 1 crédito
// de la empresa cada vez que se encuentra una cita.
const API_BASE = process.env.REACT_APP_API_URL || '';
const API_ITV = `${API_BASE}/api/adelantador-itv`;

// URL directa a itvcita.com para cambiar cita
const ITV_URL = 'https://www.itvcita.com/Welcome.do';

// AudioContext persistente para el bucle de alerta
let _audioCtx = null;
let _loopTimeout = null;
let _loopActivo = false;

function getAudioCtx() {
  if (!_audioCtx || _audioCtx.state === 'closed') {
    _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  }
  if (_audioCtx.state === 'suspended') {
    _audioCtx.resume();
  }
  return _audioCtx;
}

// Toca una secuencia de pitidos urgentes y la encadena en bucle
function _tocarSecuencia() {
  if (!_loopActivo) return;
  try {
    const ctx = getAudioCtx();
    const secuencia = [
      { f: 1400, dur: 0.12 },
      { f: 1400, dur: 0.12 },
      { f: 1400, dur: 0.12 },
      { f: 1000, dur: 0.25 },
      { f: 1400, dur: 0.25 },
    ];
    const PAUSA = 0.06;
    let t = ctx.currentTime;
    let duracionTotal = 0;
    secuencia.forEach(({ f, dur }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'square';
      osc.frequency.value = f;
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(1.0, t + 0.01);
      gain.gain.setValueAtTime(1.0, t + dur - 0.02);
      gain.gain.linearRampToValueAtTime(0, t + dur);
      osc.start(t);
      osc.stop(t + dur);
      t += dur + PAUSA;
      duracionTotal += dur + PAUSA;
    });
    _loopTimeout = setTimeout(_tocarSecuencia, (duracionTotal + 0.3) * 1000);
  } catch (_) {}
}

function iniciarSonidoBucle() {
  if (_loopActivo) return;
  _loopActivo = true;
  _tocarSecuencia();
}

function detenerSonidoBucle() {
  _loopActivo = false;
  clearTimeout(_loopTimeout);
  _loopTimeout = null;
}

function pedirPermisoNotificacion() {
  if ('Notification' in window && Notification.permission === 'default') {
    Notification.requestPermission();
  }
}

function enviarNotificacion(texto) {
  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification('¡Cita ITV disponible!', {
      body: texto,
      icon: '/favicon.ico',
    });
  }
}

export default function AdelantadorITV() {
  const [form, setForm] = useState({ localizador: '', matricula: '', fechaTope: '' });
  const [errores, setErrores] = useState({});
  const [errorAPI, setErrorAPI] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [streamToken, setStreamToken] = useState(null);
  const [activo, setActivo] = useState(false);
  const [logs, setLogs] = useState([]);
  const [alerta, setAlerta] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [alertaSonando, setAlertaSonando] = useState(false);
  const [fase, setFase] = useState('form'); // 'form' | 'estaciones' | 'activo'
  const [cargandoEstaciones, setCargandoEstaciones] = useState(false);
  const [estaciones, setEstaciones] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [estacionSeleccionada, setEstacionSeleccionada] = useState(null);
  const [copiadoLoc, setCopiadoLoc] = useState(false);
  const [copiadoMat, setCopiadoMat] = useState(false);

  // Créditos de la empresa (tope de citas que aún puede conseguir)
  const [creditos, setCreditos] = useState(null);     // número | null (ilimitado)
  const [ilimitado, setIlimitado] = useState(false);
  const [cargandoCreditos, setCargandoCreditos] = useState(true);
  const [sinCreditos, setSinCreditos] = useState(false);

  const eventSourceRef = useRef(null);
  const logsEndRef = useRef(null);

  const cargarCreditos = useCallback(async () => {
    setCargandoCreditos(true);
    try {
      const { data } = await api.get('/adelantador-itv/creditos');
      setIlimitado(!!data.ilimitado);
      setCreditos(data.ilimitado ? null : (data.creditos || 0));
      setSinCreditos(!data.ilimitado && (data.creditos || 0) <= 0);
    } catch (_) {
      setCreditos(0);
      setSinCreditos(true);
    } finally {
      setCargandoCreditos(false);
    }
  }, []);

  useEffect(() => {
    cargarCreditos();
  }, [cargarCreditos]);

  useEffect(() => {
    pedirPermisoNotificacion();
    return () => {
      detenerSonidoBucle();
      if (eventSourceRef.current) eventSourceRef.current.close();
    };
  }, []);

  useEffect(() => {
    if (logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs]);

  const agregarLog = useCallback((tipo, msg, ts) => {
    setLogs((prev) => [...prev.slice(-199), { tipo, msg, ts, id: Date.now() + Math.random() }]);
  }, []);

  const iniciarAlertaContinua = useCallback((fechaTexto) => {
    setAlerta(fechaTexto);
    setAlertaSonando(true);
    enviarNotificacion(fechaTexto);
    iniciarSonidoBucle();
  }, []);

  const detenerAlerta = useCallback(() => {
    detenerSonidoBucle();
    setAlertaSonando(false);
  }, []);

  const copiarAlPortapapeles = useCallback((texto, setCopied) => {
    navigator.clipboard.writeText(texto).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  }, []);

  const conectarSSE = useCallback((sid, token) => {
    if (eventSourceRef.current) eventSourceRef.current.close();
    const es = new EventSource(`${API_ITV}/stream/${sid}?token=${encodeURIComponent(token)}`);
    eventSourceRef.current = es;

    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.tipo === 'log') {
          agregarLog('info', data.msg, data.ts);
        } else if (data.tipo === 'error') {
          agregarLog('error', data.msg, data.ts);
        } else if (data.tipo === 'alerta') {
          agregarLog('alerta', `¡CITA ENCONTRADA! ${data.msg}`, data.ts);
          if (typeof data.creditosRestantes === 'number') {
            setCreditos(data.creditosRestantes);
            if (data.creditosRestantes <= 0) setSinCreditos(true);
          }
          iniciarAlertaContinua(data.msg);
        } else if (data.tipo === 'sincreditos') {
          agregarLog('error', data.msg, data.ts);
          setCreditos(0);
          setSinCreditos(true);
          setActivo(false);
          es.close();
        } else if (data.tipo === 'detenido') {
          agregarLog('info', 'Monitor detenido por el servidor', data.ts);
          setActivo(false);
          es.close();
        }
      } catch (_) {}
    };

    es.onerror = () => {
      agregarLog('error', 'Conexión con el servidor interrumpida', new Date().toLocaleTimeString('es-ES'));
    };
  }, [agregarLog, iniciarAlertaContinua]);

  function validarForm() {
    const errs = {};
    if (!form.localizador || form.localizador.length !== 4) {
      errs.localizador = 'Debe tener exactamente 4 caracteres';
    }
    if (!form.matricula || !/^[A-Za-z0-9]{4,10}$/.test(form.matricula)) {
      errs.matricula = 'Matrícula inválida (4-10 caracteres alfanuméricos)';
    }
    if (!form.fechaTope) {
      errs.fechaTope = 'Selecciona una fecha límite';
    } else {
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);
      if (new Date(form.fechaTope) < hoy) {
        errs.fechaTope = 'La fecha límite no puede ser en el pasado';
      }
    }
    setErrores(errs);
    return Object.keys(errs).length === 0;
  }

  function mensajeErrorAmigable(status) {
    if (status === 429) return 'Ya has utilizado la herramienta para esta matrícula hoy. Vuelve mañana.';
    if (status === 503) return 'El servicio está ocupado en este momento. Inténtalo en unos minutos.';
    if (status === 400) return 'Los datos introducidos no son válidos. Revisa el localizador, la matrícula y la fecha.';
    return 'Ha ocurrido un error. Inténtalo de nuevo.';
  }

  async function preparar(e) {
    e.preventDefault();
    if (!validarForm()) return;
    setCargando(true);
    setErrorAPI(null);
    setLogs([]);
    setAlerta(null);
    detenerAlerta();
    setEstaciones([]);
    setEstacionSeleccionada(null);
    setFiltro('');

    try {
      // Convertir de yyyy-MM-dd (input date) a dd/MM/yyyy (formato backend)
      const [anio, mes, dia] = form.fechaTope.split('-');
      const fechaTopeFormateada = `${dia}/${mes}/${anio}`;

      const { data: json } = await api.post('/adelantador-itv/preparar', {
        localizador: form.localizador,
        matricula: form.matricula.toUpperCase(),
        fechaTope: fechaTopeFormateada,
      });
      const sid = json.sessionId;
      setStreamToken(json.streamToken);
      setSessionId(sid);
      setFase('estaciones');
      setCargandoEstaciones(true);
      api.get(`/adelantador-itv/estaciones/${sid}`)
        .then(({ data: jsonE }) => {
          setEstaciones(jsonE.estaciones || []);
        })
        .catch((errE) => {
          setErrorAPI(`Error cargando estaciones: ${errE.response?.data?.error || errE.message}`);
        })
        .finally(() => setCargandoEstaciones(false));
    } catch (err) {
      const status = err.response?.status;
      const body = err.response?.data;
      if (status === 403 && body?.error === 'tope') {
        setSinCreditos(true);
        setCreditos(0);
        setFase('form');
      } else if (status) {
        setErrorAPI(body?.mensaje || mensajeErrorAmigable(status));
      } else {
        setErrorAPI('No se pudo conectar con el servidor. Comprueba tu conexión e inténtalo de nuevo.');
      }
    } finally {
      setCargando(false);
    }
  }

  async function iniciarMonitor() {
    if (!sessionId || !estacionSeleccionada) return;
    setCargando(true);
    try {
      const { data: json } = await api.post('/adelantador-itv/iniciar', {
        sessionId,
        idEstacion: estacionSeleccionada.id,
      });
      setFase('activo');
      setActivo(true);
      agregarLog('info', json.mensaje, new Date().toLocaleTimeString('es-ES'));
      conectarSSE(sessionId, streamToken);
    } catch (err) {
      const body = err.response?.data;
      if (err.response?.status === 403 && body?.error === 'tope') {
        setSinCreditos(true);
        setCreditos(0);
        setFase('form');
      }
      agregarLog('error', body?.mensaje || body?.error || err.message, new Date().toLocaleTimeString('es-ES'));
    } finally {
      setCargando(false);
    }
  }

  async function detener() {
    if (!sessionId) return;
    detenerAlerta();
    try {
      await api.delete(`/adelantador-itv/detener/${sessionId}`);
    } catch (_) {}
    if (eventSourceRef.current) eventSourceRef.current.close();
    setActivo(false);
    setFase('form');
    setSessionId(null);
    setStreamToken(null);
    setEstaciones([]);
    setEstacionSeleccionada(null);
    setFiltro('');
    agregarLog('info', 'Monitor detenido', new Date().toLocaleTimeString('es-ES'));
  }

  const colorLog = { info: '#c9d1d9', error: '#f85149', alerta: '#3fb950' };

  return (
    <div style={styles.page}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0% { opacity: 1; } 50% { opacity: .45; } 100% { opacity: 1; } }
      `}</style>
      <div style={styles.card}>
        <h1 style={styles.titulo}>🚗 Adelantador de cita ITV</h1>

        {/* Indicador de créditos */}
        {!cargandoCreditos && (
          <div style={styles.creditosBox}>
            <span style={styles.creditosLabel}>Créditos disponibles</span>
            <span style={{ ...styles.creditosValor, ...((!ilimitado && creditos <= 0) ? styles.creditosCero : {}) }}>
              {ilimitado ? '∞ (ilimitado)' : creditos}
            </span>
          </div>
        )}

        {/* Pantalla de tope superado: no hay créditos (salvo que haya una alerta activa que mostrar) */}
        {!cargandoCreditos && sinCreditos && !alerta ? (
          <div style={styles.topeBox}>
            <div style={styles.topeIcono}>🔒</div>
            <strong style={{ fontSize: 16 }}>Has alcanzado el tope de créditos ITV</strong>
            <p style={styles.topeTexto}>
              Tu empresa ha consumido todos los créditos disponibles para el adelantador de citas ITV.
              Para conseguir más créditos y seguir usando esta funcionalidad, <strong>contacta con atención al cliente</strong>.
            </p>
          </div>
        ) : (
        <>
        <div style={styles.infoBox}>
          <div style={styles.infoIcono}>ℹ️</div>
          <div style={styles.infoTexto}>
            <strong>¿Cómo funciona?</strong>
            <ul style={styles.infoLista}>
              <li>Necesitas tener una <strong>cita ya reservada</strong> en itvcita.com.</li>
              <li>Con tu <strong>localizador</strong> y <strong>matrícula</strong> el monitor comprobará cada 30 segundos si hay una cita disponible antes de tu fecha límite en la estación que elijas.</li>
              <li>Cada vez que se <strong>encuentra una cita</strong> se descuenta <strong>1 crédito</strong> de tu empresa.</li>
              <li>Si encuentra una cita disponible, <strong>emitirá un sonido de alerta</strong> para que puedas entrar rápidamente a reservarla.</li>
            </ul>
          </div>
        </div>

        {errorAPI && (
          <div style={styles.errorApiBanner}>
            <span style={{ fontSize: 18, flexShrink: 0 }}>⚠️</span>
            <div style={{ flex: 1 }}>{errorAPI}</div>
            <button style={styles.btnCerrarError} onClick={() => setErrorAPI(null)}>✕</button>
          </div>
        )}

        {alerta && (
          <div style={styles.alertaBanner}>
            <div style={styles.alertaTop}>
              <span style={styles.alertaIcono}>🎉</span>
              <div style={{ flex: 1 }}>
                <strong style={{ fontSize: 16 }}>¡CITA DISPONIBLE!</strong>
                <div style={{ marginTop: 4, fontSize: 14 }}>{alerta}</div>
              </div>
              {alertaSonando && (
                <button style={styles.btnSilencio} onClick={detenerAlerta}>
                  🔇 Silenciar
                </button>
              )}
            </div>
            <div style={styles.alertaAcciones}>
              <a
                href={ITV_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={styles.btnCambiarCita}
                onClick={detenerAlerta}
              >
                🔗 Cambiar cita en itvcita.com
              </a>
              <div style={styles.alertaDatos}>
                <span style={styles.alertaDatoLabel}>Localizador:</span>
                <code style={styles.alertaDatoCod}>{form.localizador}</code>
                <button
                  style={styles.btnCopiar}
                  onClick={() => copiarAlPortapapeles(form.localizador, setCopiadoLoc)}
                >
                  {copiadoLoc ? '✓ Copiado' : '📋 Copiar'}
                </button>
              </div>
              <div style={styles.alertaDatos}>
                <span style={styles.alertaDatoLabel}>Matrícula:</span>
                <code style={styles.alertaDatoCod}>{form.matricula.toUpperCase()}</code>
                <button
                  style={styles.btnCopiar}
                  onClick={() => copiarAlPortapapeles(form.matricula.toUpperCase(), setCopiadoMat)}
                >
                  {copiadoMat ? '✓ Copiado' : '📋 Copiar'}
                </button>
              </div>
              <div style={styles.alertaInstruccion}>
                Abre el enlace, selecciona <strong>Modificar cita</strong> e introduce estos datos para completar el cambio.
              </div>
            </div>
          </div>
        )}

        <form onSubmit={preparar} style={styles.form}>
          <div style={styles.campo}>
            <label style={styles.label}>Localizador (4 caracteres)</label>
            <input
              style={{ ...styles.input, ...(errores.localizador ? styles.inputError : {}) }}
              value={form.localizador}
              onChange={(e) => setForm((f) => ({ ...f, localizador: e.target.value }))}
              maxLength={4}
              placeholder="Ej: AB12"
              disabled={fase !== 'form'}
            />
            {errores.localizador && <span style={styles.errorMsg}>{errores.localizador}</span>}
          </div>

          <div style={styles.campo}>
            <label style={styles.label}>Matrícula</label>
            <input
              style={{ ...styles.input, ...(errores.matricula ? styles.inputError : {}) }}
              value={form.matricula}
              onChange={(e) => setForm((f) => ({ ...f, matricula: e.target.value.toUpperCase() }))}
              placeholder="Ej: 4808JMR"
              disabled={fase !== 'form'}
            />
            {errores.matricula && <span style={styles.errorMsg}>{errores.matricula}</span>}
          </div>

          <div style={styles.campo}>
            <label style={styles.label}>Fecha tope</label>
            <input
              type="date"
              style={{ ...styles.input, ...(errores.fechaTope ? styles.inputError : {}), colorScheme: 'dark' }}
              value={form.fechaTope}
              min={new Date().toISOString().split('T')[0]}
              onChange={(e) => setForm((f) => ({ ...f, fechaTope: e.target.value }))}
              disabled={fase !== 'form'}
            />
            {errores.fechaTope && <span style={styles.errorMsg}>{errores.fechaTope}</span>}
          </div>

          {fase === 'form' && (
            <button type="submit" style={styles.btnEscoger} disabled={cargando}>
              {cargando ? 'Conectando…' : '🔍 Escoger estación'}
            </button>
          )}
        </form>

        {fase === 'estaciones' && (
          <div style={styles.estacionesSeccion}>
            {cargandoEstaciones ? (
              <div style={styles.spinnerWrap}>
                <div style={styles.spinnerBorder} />
                <span style={styles.spinnerTxt}>Cargando estaciones ITV…</span>
              </div>
            ) : (
              <>
                <div style={styles.filtroWrap}>
                  <input
                    style={styles.filtroInput}
                    placeholder="Filtrar por nombre o ID…"
                    value={filtro}
                    onChange={(e) => setFiltro(e.target.value)}
                    autoFocus
                  />
                  <span style={styles.filtroCount}>
                    {filtro.trim()
                      ? `${estaciones.filter((e) => e.nombre.toLowerCase().includes(filtro.toLowerCase()) || e.id.includes(filtro)).length} de ${estaciones.length}`
                      : `${estaciones.length} estaciones`}
                  </span>
                </div>
                <div style={styles.estacionesList}>
                  {(filtro.trim()
                    ? estaciones.filter((e) => e.nombre.toLowerCase().includes(filtro.toLowerCase()) || e.id.includes(filtro))
                    : estaciones
                  ).map((est) => (
                    <div
                      key={est.id}
                      style={{
                        ...styles.estacionItem,
                        ...(estacionSeleccionada?.id === est.id ? styles.estacionItemActivo : {}),
                      }}
                      onClick={() => setEstacionSeleccionada(est)}
                    >
                      <span style={styles.estacionNombre}>{est.nombre}</span>
                      <span style={styles.estacionId}>ID {est.id}</span>
                    </div>
                  ))}
                  {filtro.trim() &&
                    estaciones.filter((e) => e.nombre.toLowerCase().includes(filtro.toLowerCase()) || e.id.includes(filtro)).length === 0 && (
                      <div style={styles.sinResultados}>Sin resultados para "{filtro}"</div>
                    )}
                </div>
                <div style={styles.estacionesAcciones}>
                  {estacionSeleccionada && (
                    <button style={styles.btnIniciar} disabled={cargando} onClick={iniciarMonitor}>
                      {cargando ? 'Iniciando…' : `▶ Iniciar con ${estacionSeleccionada.nombre}`}
                    </button>
                  )}
                  <button
                    style={styles.btnVolver}
                    onClick={() => { setFase('form'); setSessionId(null); setEstaciones([]); setEstacionSeleccionada(null); setFiltro(''); }}
                  >
                    ← Volver
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {fase === 'activo' && (
          <>
            {estacionSeleccionada && (
              <div style={styles.estacionActivaBadge}>
                📍 {estacionSeleccionada.nombre}
              </div>
            )}
            <div style={styles.estadoBadge}>
              <span style={styles.pulsoDot} /> Monitorizando — consulta cada 30 s
            </div>
            <button style={{ ...styles.btnDetener, marginTop: 12, width: '100%' }} onClick={detener}>
              ⏹ Detener monitor
            </button>
          </>
        )}
        </>
        )}

        {logs.length > 0 && (
          <div style={styles.logBox}>
            <div style={styles.logHeader}>Registro de actividad</div>
            {logs.map((l) => (
              <div key={l.id} style={{ ...styles.logLinea, color: colorLog[l.tipo] || '#c9d1d9' }}>
                <span style={styles.logTs}>[{l.ts}]</span> {l.msg}
              </div>
            ))}
            <div ref={logsEndRef} />
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: '#0d1117',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '40px 16px',
    fontFamily: "'Segoe UI', system-ui, sans-serif",
  },
  card: {
    background: '#161b22',
    border: '1px solid #30363d',
    borderRadius: 12,
    padding: '32px 28px',
    width: '100%',
    maxWidth: 560,
    color: '#e6edf3',
  },
  titulo: {
    margin: '0 0 14px',
    fontSize: 22,
    fontWeight: 700,
    color: '#58a6ff',
  },
  creditosBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: '#0d1117',
    border: '1px solid #30363d',
    borderRadius: 8,
    padding: '10px 14px',
    marginBottom: 18,
  },
  creditosLabel: { fontSize: 13, color: '#8b949e', fontWeight: 500 },
  creditosValor: { fontSize: 18, fontWeight: 700, color: '#3fb950' },
  creditosCero: { color: '#f85149' },
  topeBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: 8,
    background: '#2a1e0f',
    border: '1px solid #d29922',
    borderRadius: 8,
    padding: '24px 20px',
    color: '#e3b341',
  },
  topeIcono: { fontSize: 34 },
  topeTexto: { fontSize: 13, color: '#c9b48a', lineHeight: 1.6, margin: 0 },
  infoBox: {
    display: 'flex',
    gap: 12,
    background: '#0d1b2a',
    border: '1px solid #1f6feb',
    borderRadius: 8,
    padding: '14px 16px',
    marginBottom: 24,
  },
  infoIcono: { fontSize: 18, flexShrink: 0, marginTop: 1 },
  infoTexto: { fontSize: 13, color: '#8b949e', lineHeight: 1.6 },
  infoLista: { margin: '6px 0 0 0', paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 4 },
  errorApiBanner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    background: '#2a0f0f',
    border: '1px solid #f85149',
    borderRadius: 8,
    padding: '12px 14px',
    marginBottom: 16,
    color: '#ffa198',
    fontSize: 13,
    lineHeight: 1.5,
  },
  btnCerrarError: {
    background: 'transparent',
    border: 'none',
    color: '#f85149',
    cursor: 'pointer',
    fontSize: 14,
    flexShrink: 0,
    padding: '0 2px',
  },
  alertaBanner: {
    background: '#0f2a13',
    border: '1px solid #3fb950',
    borderRadius: 8,
    padding: '14px 16px',
    marginBottom: 20,
    color: '#3fb950',
    fontWeight: 600,
    animation: 'pulse 1s infinite',
  },
  alertaTop: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
  },
  alertaAcciones: {
    marginTop: 14,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  btnCambiarCita: {
    display: 'inline-block',
    background: '#238636',
    border: '1px solid #2ea043',
    color: '#fff',
    borderRadius: 6,
    padding: '10px 16px',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    textDecoration: 'none',
    textAlign: 'center',
  },
  alertaDatos: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  alertaDatoLabel: {
    fontSize: 13,
    color: '#8b949e',
    fontWeight: 500,
  },
  alertaDatoCod: {
    background: '#0d1117',
    border: '1px solid #30363d',
    borderRadius: 4,
    padding: '3px 8px',
    fontSize: 14,
    letterSpacing: 1,
    color: '#f0f6fc',
    fontFamily: 'monospace',
  },
  btnCopiar: {
    background: 'transparent',
    border: '1px solid #3fb950',
    color: '#3fb950',
    borderRadius: 5,
    padding: '3px 10px',
    cursor: 'pointer',
    fontSize: 12,
  },
  alertaInstruccion: {
    fontSize: 12,
    color: '#8b949e',
    lineHeight: 1.5,
    borderTop: '1px solid #1e3a22',
    paddingTop: 10,
    marginTop: 2,
  },
  alertaIcono: { fontSize: 28 },
  btnSilencio: {
    marginLeft: 'auto',
    background: 'transparent',
    border: '1px solid #3fb950',
    color: '#3fb950',
    borderRadius: 6,
    padding: '4px 10px',
    cursor: 'pointer',
    fontSize: 12,
  },
  form: { display: 'flex', flexDirection: 'column', gap: 16 },
  campo: { display: 'flex', flexDirection: 'column', gap: 4 },
  label: { fontSize: 13, color: '#8b949e', fontWeight: 500 },
  input: {
    background: '#0d1117',
    border: '1px solid #30363d',
    borderRadius: 6,
    padding: '9px 12px',
    color: '#e6edf3',
    fontSize: 14,
    outline: 'none',
  },
  inputError: { borderColor: '#f85149' },
  errorMsg: { fontSize: 11, color: '#f85149' },
  btnIniciar: {
    background: '#238636',
    border: '1px solid #2ea043',
    color: '#fff',
    borderRadius: 6,
    padding: '10px 0',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 4,
  },
  btnDetener: {
    background: '#b62324',
    border: '1px solid #f85149',
    color: '#fff',
    borderRadius: 6,
    padding: '10px 0',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 4,
  },
  estadoBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 16,
    fontSize: 12,
    color: '#3fb950',
  },
  pulsoDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: '#3fb950',
    display: 'inline-block',
    animation: 'pulse 1.2s ease-in-out infinite',
  },
  logBox: {
    marginTop: 20,
    background: '#0d1117',
    border: '1px solid #30363d',
    borderRadius: 8,
    maxHeight: 280,
    overflowY: 'auto',
    fontSize: 12,
    fontFamily: 'monospace',
  },
  logHeader: {
    padding: '6px 12px',
    borderBottom: '1px solid #30363d',
    color: '#8b949e',
    fontSize: 11,
    fontFamily: 'inherit',
  },
  logLinea: {
    padding: '3px 12px',
    lineHeight: 1.6,
    wordBreak: 'break-word',
  },
  logTs: { color: '#6e7681' },
  btnEscoger: {
    background: '#1f6feb',
    border: '1px solid #388bfd',
    color: '#fff',
    borderRadius: 6,
    padding: '10px 0',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 4,
  },
  estacionesSeccion: {
    marginTop: 20,
  },
  spinnerWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12,
    padding: '28px 0',
  },
  spinnerBorder: {
    width: 36,
    height: 36,
    border: '3px solid #30363d',
    borderTopColor: '#58a6ff',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  spinnerTxt: {
    color: '#8b949e',
    fontSize: 13,
  },
  filtroWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 10,
  },
  filtroInput: {
    flex: 1,
    background: '#0d1117',
    border: '1px solid #30363d',
    borderRadius: 6,
    padding: '8px 12px',
    color: '#e6edf3',
    fontSize: 13,
    outline: 'none',
  },
  filtroCount: {
    fontSize: 11,
    color: '#6e7681',
    whiteSpace: 'nowrap',
  },
  estacionesList: {
    maxHeight: 240,
    overflowY: 'auto',
    border: '1px solid #30363d',
    borderRadius: 8,
    display: 'flex',
    flexDirection: 'column',
  },
  estacionItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '9px 14px',
    cursor: 'pointer',
    borderBottom: '1px solid #21262d',
  },
  estacionItemActivo: {
    background: '#0f2a4a',
    borderLeft: '3px solid #58a6ff',
  },
  estacionNombre: { fontSize: 13, color: '#e6edf3' },
  estacionId: { fontSize: 11, color: '#6e7681' },
  sinResultados: {
    padding: 14,
    textAlign: 'center',
    color: '#6e7681',
    fontSize: 13,
  },
  estacionesAcciones: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginTop: 12,
  },
  btnVolver: {
    background: 'transparent',
    border: '1px solid #30363d',
    color: '#8b949e',
    borderRadius: 6,
    padding: '8px 0',
    fontSize: 13,
    cursor: 'pointer',
  },
  estacionActivaBadge: {
    marginTop: 14,
    marginBottom: 10,
    padding: '8px 12px',
    background: '#0f2a4a',
    border: '1px solid #1f6feb',
    borderRadius: 6,
    fontSize: 13,
    color: '#58a6ff',
  },
};
